<template>
    <div class="xlsx-handler">
        <h4 class="xlsx-handler__title">
            Configuration du fichier Excel
        </h4>
        <div
            v-if="currentSheet && currentSheet.rows && currentSheet.rows.length"
            class="xlsx-handler__preview"
        >
            <h5>Prévisualisation de l'onglet sélectionné</h5>
            <div class="xlsx-handler__table-wrapper">
                <table class="xlsx-handler__table">
                    <tbody>
                        <tr
                            v-for="rowInfo in previewRows"
                            :key="`row-${rowInfo.index}`"
                            :class="{
                                'is-header-row': rowInfo.index === 0,
                                'is-first-data-row': rowInfo.index + 1 === selectedFirstDataRow
                            }"
                        >
                            <td
                                v-for="(cell, cIndex) in rowInfo.row"
                                :key="`cell-${rowInfo.index}-${cIndex}`"
                                :class="{
                                    'is-date-column': cIndex === selectedDateColumn,
                                    'is-time-column': dateTimeMode === 'split' && cIndex === selectedTimeColumn,
                                    'is-measure-column': cIndex === selectedMeasureColumn
                                }"
                            >
                                {{ formatCell(cell) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="xlsx-handler__controls">
            <el-form
                :inline="true"
                label-width="220px"
                label-position="left"
                size="small"
            >
                <div class="xlsx-handler__controls-group">
                    <el-form-item label="Onglet">
                        <el-select
                            v-model="selectedSheetName"
                            placeholder="Sélectionner un onglet"
                        >
                            <el-option
                                v-for="sheet in sheets"
                                :key="sheet.name"
                                :label="sheet.name"
                                :value="sheet.name"
                            />
                        </el-select>
                    </el-form-item>
    
                    <el-form-item label="Première ligne de données">
                        <el-select
                            v-model="selectedFirstDataRow"
                            placeholder="Sélectionner la ligne"
                        >
                            <el-option
                                v-for="rowIndex in availableRowIndexes"
                                :key="rowIndex"
                                :label="`Ligne ${rowIndex}`"
                                :value="rowIndex"
                            />
                        </el-select>
                    </el-form-item>
                </div>
                <el-form-item label="Type de colonne date/heure">
                    <el-radio-group v-model="dateTimeMode">
                        <el-radio label="single">
                            Date + heure dans une seule colonne
                        </el-radio>
                        <el-radio label="split">
                            Date et heure dans deux colonnes
                        </el-radio>
                    </el-radio-group>
                </el-form-item>
                <div class="xlsx-handler__controls-group">
                    <el-form-item label="Colonne date">
                        <el-select
                            v-model="selectedDateColumn"
                            placeholder="Sélectionner la colonne"
                        >
                            <el-option
                                v-for="col in columnOptions"
                                :key="col.value"
                                :label="col.label"
                                :value="col.value"
                            />
                        </el-select>
                    </el-form-item>
    
                    <el-form-item
                        label="Format de date"
                        :class="{'xlsx-handler__controls-item': selectedDateFormat === 'Autres'}"
                    >
                        <el-select
                            v-model="selectedDateFormat"
                            placeholder="Sélectionner un format"
                        >
                            <el-option
                                v-for="format in dateFormatOptions"
                                :key="format"
                                :label="format"
                                :value="format"
                            />
                        </el-select>
                        <el-input
                            v-if="selectedDateFormat === 'Autres'"
                            v-model="customDateFormat"
                            placeholder="Saisir un format de date (ex: DD/MM/YYYY HH:mm)"
                            size="small"
                            class="xlsx-handler__custom-format"
                        />
                    </el-form-item>
                </div>
                <div class="xlsx-handler__controls-group">
                    <el-form-item
                        v-if="dateTimeMode === 'split'"
                        label="Colonne heure"
                    >
                        <el-select
                            v-model="selectedTimeColumn"
                            placeholder="Sélectionner la colonne"
                        >
                            <el-option
                                v-for="col in columnOptions"
                                :key="col.value"
                                :label="col.label"
                                :value="col.value"
                            />
                        </el-select>
                    </el-form-item>

                    <el-form-item
                        v-if="dateTimeMode === 'split'"
                        label="Format de l'heure"
                        :class="{'xlsx-handler__controls-item': selectedTimeFormat === 'Autres'}"
                    >
                        <el-select
                            v-model="selectedTimeFormat"
                            placeholder="Sélectionner un format"
                        >
                            <el-option
                                v-for="format in timeFormatOptions"
                                :key="format"
                                :label="format"
                                :value="format"
                            />
                        </el-select>
                        <el-input
                            v-if="selectedTimeFormat === 'Autres'"
                            v-model="customTimeFormat"
                            placeholder="Saisir un format d'heure (ex: HH:mm:ss)"
                            size="small"
                            class="xlsx-handler__custom-format"
                        />
                    </el-form-item>
                </div>

                <div class="xlsx-handler__controls-group">
                    <el-form-item label="Colonne mesure">
                        <el-select
                            v-model="selectedMeasureColumn"
                            placeholder="Sélectionner la colonne"
                        >
                            <el-option
                                v-for="col in columnOptions"
                                :key="col.value"
                                :label="col.label"
                                :value="col.value"
                            />
                        </el-select>
                    </el-form-item>
    
                    <el-form-item label="Unité de la mesure">
                        <el-select
                            v-model="selectedMeasureUnit"
                            placeholder="Sélectionner l'unité"
                        >
                            <el-option
                                v-for="unit in measureUnits"
                                :key="unit"
                                :label="unit"
                                :value="unit"
                            />
                        </el-select>
                    </el-form-item>
                </div>

                <el-form-item>
                    <el-button
                        type="primary"
                        :disabled="!isValid"
                        @click="emitConfig"
                    >
                        Valider la configuration
                    </el-button>
                </el-form-item>
            </el-form>
        </div>
    </div>
</template>

<script>
export default {

    name: 'XlsxHandler',

    props: {
        sheets: {
            type: Array,
            required: true
        }
    },

    data () {
        return {
            selectedSheetName: null,
            selectedFirstDataRow: null,
            selectedDateColumn: null,
            selectedDateFormat: null,
            dateTimeMode: 'single',
            selectedTimeColumn: null,
            selectedTimeFormat: null,
            selectedMeasureColumn: null,
            selectedMeasureUnit: null,
            customDateFormat: '',
            customTimeFormat: '',
            measureUnits: ['A', 'kW', 'W'],
            knownDateTimeFormats: [
                'DD/MM/YYYY HH:mm',
                'DD/MM/YYYY HH:mm:ss',
                'YYYY-MM-DD HH:mm',
                'YYYY-MM-DD HH:mm:ss'
            ],
            knownDateOnlyFormats: [
                'DD/MM/YYYY',
                'YYYY-MM-DD'
            ],
            knownTimeFormats: [
                'HH:mm',
                'HH:mm:ss'
            ]
        };
    },

    computed: {
        currentSheet () {
            if (!this.selectedSheetName) return null;
            return this.sheets.find(sheet => sheet.name === this.selectedSheetName) || null;
        },
        limitedRows () {
            if (!this.currentSheet || !this.currentSheet.rows) return [];
            // Only keep the first 10 rows for all preview / selection logic
            return this.currentSheet.rows.slice(0, 10);
        },
        availableRowIndexes () {
            if (!this.limitedRows.length) return [];
            return this.limitedRows.map((row, index) => index + 1);
        },
        columnOptions () {
            if (!this.limitedRows.length) {
                return [];
            }
            // Determine the maximum number of columns across the limited rows so that
            // columns are selectable even if the first row is mostly empty.
            const maxColumns = this.limitedRows.reduce((max, row) => {
                return Math.max(max, Array.isArray(row) ? row.length : 0);
            }, 0);

            const options = [];
            for (let index = 0; index < maxColumns; index++) {
                // Try to find a non-empty sample value for this column to show in the label
                let sampleValue = null;
                for (let r = 0; r < this.limitedRows.length; r++) {
                    const cell = this.limitedRows[r][index];
                    if (cell !== null && cell !== undefined && cell !== '') {
                        sampleValue = cell;
                        break;
                    }
                }

                let label = `Colonne ${index + 1}`;
                if (sampleValue !== null) {
                    label += ` (${sampleValue})`;
                }

                options.push({
                    value: index,
                    label
                });
            }
            return options;
        },
        dateFormatOptions () {
            const base = this.dateTimeMode === 'single'
                ? this.knownDateTimeFormats
                : this.knownDateOnlyFormats;
            return [...base, 'Autres'];
        },
        timeFormatOptions () {
            return [...this.knownTimeFormats, 'Autres'];
        },
        previewRows () {
            if (!this.limitedRows.length) return [];
            // Use index within the limited rows (1..10) consistently for highlighting
            return this.limitedRows.map((row, index) => ({ row, index }));
        },
        isValid () {
            if (
                !this.selectedSheetName ||
                !this.selectedFirstDataRow ||
                this.selectedMeasureColumn === null ||
                !this.selectedMeasureUnit
            ) {
                return false;
            }
            if (
                this.selectedDateColumn === null ||
                !this.selectedDateFormat
            ) {
                return false;
            }
            if (this.selectedDateFormat === 'Autres' && !this.customDateFormat) {
                return false;
            }
            if (this.dateTimeMode === 'split') {
                if (
                    this.selectedTimeColumn === null ||
                    !this.selectedTimeFormat
                ) {
                    return false;
                }
                if (this.selectedTimeFormat === 'Autres' && !this.customTimeFormat) {
                    return false;
                }
            }
            return true;
        }
    },

    mounted () {
        if (this.sheets && this.sheets.length) {
            this.selectedSheetName = this.sheets[0].name;
        }
    },

    methods: {
        emitConfig () {
            if (!this.isValid) {
                return;
            }
            const effectiveDateFormat = this.selectedDateFormat === 'Autres'
                ? this.customDateFormat
                : this.selectedDateFormat;
            const payload = {
                sheetName: this.selectedSheetName,
                firstDataRow: this.selectedFirstDataRow,
                dateColumnIndex: this.selectedDateColumn,
                dateFormat: effectiveDateFormat,
                measureColumnIndex: this.selectedMeasureColumn,
                measureUnit: this.selectedMeasureUnit,
                dateTimeMode: this.dateTimeMode
            };
            if (this.dateTimeMode === 'split') {
                const effectiveTimeFormat = this.selectedTimeFormat === 'Autres'
                    ? this.customTimeFormat
                    : this.selectedTimeFormat;
                payload.timeColumnIndex = this.selectedTimeColumn;
                payload.timeFormat = effectiveTimeFormat;
            }
            this.$emit('config-confirmed', payload);
        },
        formatCell (cell) {
            if (cell === null || cell === undefined) return '';
            if (typeof cell === 'string' && cell.length > 40) {
                return `${cell.slice(0, 37)}...`;
            }
            return cell;
        }
    }
};
</script>

<style lang="scss" scoped>
.xlsx-handler {
    margin-top: 20px;
    text-align: left;
}

.xlsx-handler__custom-format {
    width: 200px;
}

.xlsx-handler__title {
    margin-bottom: 10px;
    font-weight: 600;
}

.xlsx-handler__controls {
    margin: 20px 0;
}

.xlsx-handler__controls-group {
    display: flex;
    flex-direction: row;
    gap: 12px;
}



.xlsx-handler__preview {
    margin-top: 10px;
}

.xlsx-handler__table-wrapper {
    max-height: 260px;
    overflow: auto;
    border: 1px solid #ebeef5;
}

.xlsx-handler__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.xlsx-handler__table td {
    border: 1px solid #ebeef5;
    padding: 4px 6px;
    white-space: nowrap;
}

.xlsx-handler__table .is-header-row td {
    font-weight: 600;
    background-color: #f5f7fa;
}

.xlsx-handler__table .is-first-data-row td {
    background-color: #ecf5ff;
}

.xlsx-handler__table .is-date-column {
    background-color: #fff7e6;
}

.xlsx-handler__table .is-time-column {
    background-color: #e6f7ff;
}

.xlsx-handler__table .is-measure-column {
    background-color: #f0f9eb;
}

</style>

<style>
/* Layout for form items when "Autres" is selected" (deep selector for scoped styles) */
.xlsx-handler__controls-item > .el-form-item__content {
    display: flex !important;
    flex-direction: row;
    gap: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/partials/simulation/XlsxHandler.vue